import {
  FunctionComponent,
  MouseEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState
} from "react";
import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  ToggleButton,
  ToggleButtonGroup,
  Typography
} from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";
import { DateTime } from "luxon";
import { useTranslation } from "react-i18next";
import {
  buildManagementMonthPickerOptions,
  buildManagementWeekPickerOptions,
  formatManagementPeriodLabel,
  isSameManagementMonth,
  isSameManagementWeek,
  MANAGEMENT_PERIOD_PICKER_STEP,
  ManagementPeriodMode
} from "../utils/management-period-picker.utils";

type ManagementPeriodPickerDialogProps = {
  open: boolean;
  periodMode: ManagementPeriodMode;
  periodAnchor: DateTime;
  onClose: () => void;
  onSelect: (mode: ManagementPeriodMode, anchor: DateTime) => void;
};

/**
 * Period picker: Week/Month toggle + scrollable week or month list.
 */
export const ManagementPeriodPickerDialog: FunctionComponent<ManagementPeriodPickerDialogProps> = ({
  open,
  periodMode,
  periodAnchor,
  onClose,
  onSelect
}) => {
  const { t, i18n } = useTranslation();
  const [draftMode, setDraftMode] = useState<ManagementPeriodMode>(periodMode);
  const [pickerCenter, setPickerCenter] = useState(periodAnchor);
  const [draftAnchor, setDraftAnchor] = useState(periodAnchor);
  const scrollTargetRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (open) {
      setDraftMode(periodMode);
      setPickerCenter(periodAnchor);
      setDraftAnchor(periodAnchor);
    }
  }, [open, periodAnchor, periodMode]);

  const options = useMemo(
    () =>
      draftMode === "week"
        ? buildManagementWeekPickerOptions(pickerCenter)
        : buildManagementMonthPickerOptions(pickerCenter),
    [draftMode, pickerCenter]
  );

  const scrollTargetKey = useMemo(() => {
    if (draftMode === "week") {
      const selected = options.find(week => isSameManagementWeek(week, draftAnchor));
      const target = selected ?? pickerCenter.startOf("week");
      return target.toISODate() ?? target.toISO();
    }
    const selected = options.find(month => isSameManagementMonth(month, draftAnchor));
    const target = selected ?? pickerCenter.startOf("month");
    return target.toISODate() ?? target.toISO();
  }, [draftMode, options, draftAnchor, pickerCenter]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const scrollSelectedIntoView = () => {
      const target = scrollTargetRef.current;
      if (typeof target?.scrollIntoView === "function") {
        target.scrollIntoView({ block: "center", inline: "nearest" });
      }
    };

    const frameId = requestAnimationFrame(scrollSelectedIntoView);

    return () => {
      cancelAnimationFrame(frameId);
    };
  }, [open, options, scrollTargetKey]);

  const handleDraftMode = (_: MouseEvent<HTMLElement>, value: ManagementPeriodMode | null) => {
    if (value === null) {
      return;
    }
    const nextAnchor =
      value === "week" ? DateTime.now().startOf("week") : DateTime.now().startOf("month");
    setDraftMode(value);
    setPickerCenter(nextAnchor);
    setDraftAnchor(nextAnchor);
  };

  const handlePrevious = useCallback(() => {
    setPickerCenter(prev =>
      draftMode === "week"
        ? prev.minus({ weeks: MANAGEMENT_PERIOD_PICKER_STEP }).startOf("week")
        : prev.minus({ years: 1 }).startOf("year")
    );
  }, [draftMode]);

  const handleNext = useCallback(() => {
    setPickerCenter(prev =>
      draftMode === "week"
        ? prev.plus({ weeks: MANAGEMENT_PERIOD_PICKER_STEP }).startOf("week")
        : prev.plus({ years: 1 }).startOf("year")
    );
  }, [draftMode]);

  const handleSelect = useCallback(
    (value: DateTime) => {
      const anchor = draftMode === "week" ? value.startOf("week") : value.startOf("month");
      onSelect(draftMode, anchor);
      onClose();
    },
    [draftMode, onClose, onSelect]
  );

  const centerLabel =
    draftMode === "week"
      ? t("_dashboard2_weekLabel", {
          week: pickerCenter.weekNumber,
          year: pickerCenter.year
        })
      : pickerCenter.toFormat("yyyy");

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="xs"
      data-testid="management-period-picker-dialog"
    >
      <DialogTitle>{t("_management_periodPickerTitle")}</DialogTitle>
      <DialogContent>
        <ToggleButtonGroup
          exclusive
          fullWidth
          size="small"
          value={draftMode}
          onChange={handleDraftMode}
          aria-label={t("_management_period")}
          sx={{ mb: 2 }}
        >
          <ToggleButton value="week">{t("Week")}</ToggleButton>
          <ToggleButton value="month">{t("Month")}</ToggleButton>
        </ToggleButtonGroup>

        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1
          }}
        >
          <IconButton
            aria-label={t("_management_periodPickerPrevious")}
            onClick={handlePrevious}
            data-testid="management-period-picker-previous"
          >
            <ChevronLeft />
          </IconButton>
          <Typography
            variant="subtitle1"
            component="p"
            sx={{ flex: 1, textAlign: "center", fontWeight: 600 }}
            data-testid="management-period-picker-center-label"
          >
            {centerLabel}
          </Typography>
          <IconButton
            aria-label={t("_management_periodPickerNext")}
            onClick={handleNext}
            data-testid="management-period-picker-next"
          >
            <ChevronRight />
          </IconButton>
        </Box>

        <List
          disablePadding
          sx={{ maxHeight: 280, overflow: "auto" }}
          data-testid="management-period-picker-list"
        >
          {options.map(option => {
            const optionKey = option.toISODate() ?? option.toISO();
            const isSelected =
              draftMode === "week"
                ? isSameManagementWeek(option, draftAnchor)
                : isSameManagementMonth(option, draftAnchor);

            return (
              <ListItemButton
                key={optionKey}
                ref={optionKey === scrollTargetKey ? scrollTargetRef : undefined}
                selected={isSelected}
                onClick={() => handleSelect(option)}
                data-testid={`management-period-picker-option-${optionKey}`}
              >
                <ListItemText
                  primary={
                    draftMode === "week"
                      ? t("_dashboard2_weekLabel", {
                          week: option.weekNumber,
                          year: option.year
                        })
                      : formatManagementPeriodLabel("month", option, i18n.language)
                  }
                  secondary={
                    draftMode === "week"
                      ? formatManagementPeriodLabel("week", option.startOf("week"), i18n.language)
                      : undefined
                  }
                />
              </ListItemButton>
            );
          })}
        </List>
      </DialogContent>
    </Dialog>
  );
};
